/**
 * ContractCreateModal
 * 전자계약 생성 모달
 */

import React, { useState } from 'react';

interface ContractCreateModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ContractFormData {
  title: string;
  contract_type: 'subscription' | 'onetime';
  signer_name: string;
  signer_phone: string;
  signer_email: string;
  amount: number;
  plan_name: string;
  terms: string;
  expires_days: number;
}

export const ContractCreateModal: React.FC<ContractCreateModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<ContractFormData>({
    title: '',
    contract_type: 'subscription',
    signer_name: '',
    signer_phone: '',
    signer_email: '',
    amount: 0,
    plan_name: 'Basic',
    terms: `1. 서비스 제공자는 안정적인 서비스를 제공합니다.
2. 이용자는 서비스 이용료를 매월 지정된 결제일에 납부해야 합니다.
3. 해지 시 최소 7일 전에 통보해야 합니다.
4. 개인정보는 개인정보 보호법에 따라 처리됩니다.`,
    expires_days: 7,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, signer_phone: formatted }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    if (!formData.title.trim()) {
      setError('계약 제목을 입력해주세요');
      return;
    }
    if (!formData.signer_name.trim()) {
      setError('서명자 이름을 입력해주세요');
      return;
    }
    if (!formData.signer_phone.trim()) {
      setError('서명자 연락처를 입력해주세요');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/admin/contracts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          title: formData.title,
          contract_type: formData.contract_type,
          signer_name: formData.signer_name,
          signer_phone: formData.signer_phone.replace(/-/g, ''),
          signer_email: formData.signer_email || null,
          contract_data: {
            amount: formData.amount,
            plan_name: formData.plan_name,
            terms: formData.terms,
          },
          expires_days: formData.expires_days,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || '계약 생성 실패');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">새 계약 생성</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* 기본 정보 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-700 border-b pb-2">계약 정보</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                계약 제목 *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="예: 뉴어텐드 독서실 관리 서비스 이용 계약"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  계약 유형 *
                </label>
                <select
                  name="contract_type"
                  value={formData.contract_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="subscription">정기구독</option>
                  <option value="onetime">일회성</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  서명 유효기간
                </label>
                <select
                  name="expires_days"
                  value={formData.expires_days}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={3}>3일</option>
                  <option value={7}>7일</option>
                  <option value={14}>14일</option>
                  <option value={30}>30일</option>
                </select>
              </div>
            </div>
          </div>

          {/* 서명자 정보 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-700 border-b pb-2">서명자 정보</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                서명자 이름 *
              </label>
              <input
                type="text"
                name="signer_name"
                value={formData.signer_name}
                onChange={handleChange}
                placeholder="홍길동"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  연락처 *
                </label>
                <input
                  type="tel"
                  name="signer_phone"
                  value={formData.signer_phone}
                  onChange={handlePhoneChange}
                  placeholder="010-1234-5678"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일
                </label>
                <input
                  type="email"
                  name="signer_email"
                  value={formData.signer_email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* 계약 내용 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-700 border-b pb-2">계약 내용</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  플랜명
                </label>
                <select
                  name="plan_name"
                  value={formData.plan_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="Basic">Basic</option>
                  <option value="Standard">Standard</option>
                  <option value="Premium">Premium</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  월 이용료 (원)
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="25000"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                계약 조건
              </label>
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleChange}
                rows={6}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* 버튼 */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? '생성 중...' : '계약 생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractCreateModal;
